import { useState, useEffect, useCallback, useRef } from 'react';
import './CustomBrand.css';

interface CustomBrandProps {
  apiBaseUrl: string;
}

const BRANDS = [
  {
    name: 'Luxury',
    accent: '#d4a574',
    font: 'Georgia, "Times New Roman", serif',
    weight: 400,
    style: 'italic' as const,
    text: 'This exquisite silk piece would elevate your evening ensemble beautifully.',
  },
  {
    name: 'Streetwear',
    accent: '#0284c7',
    font: 'inherit',
    weight: 700,
    style: 'normal' as const,
    text: 'This just dropped and it\u2019s selling fast \u2014 would look sick with the Jordan 1s you grabbed.',
  },
  {
    name: 'Classic',
    accent: '#525252',
    font: 'inherit',
    weight: 400,
    style: 'normal' as const,
    text: 'A timeless investment piece that pairs well with your existing wardrobe.',
  },
];

const INPUTS = [
  { label: 'Product Catalog', icon: 'bag' },
  { label: 'Brand Guidelines', icon: 'book' },
  { label: 'Tone of Voice', icon: 'speech' },
  { label: 'Style Rules', icon: 'sliders' },
] as const;

const svgProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function PillIcon({ type }: { type: string }) {
  switch (type) {
    case 'bag':
      return (
        <svg {...svgProps}>
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 01-8 0" />
        </svg>
      );
    case 'book':
      return (
        <svg {...svgProps}>
          <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
          <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
        </svg>
      );
    case 'speech':
      return (
        <svg {...svgProps}>
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      );
    case 'sliders':
      return (
        <svg {...svgProps}>
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      );
    default:
      return null;
  }
}

export function CustomBrand(_props: CustomBrandProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [pillCount, setPillCount] = useState(0);
  const [showInputs, setShowInputs] = useState(false);
  const [showCore, setShowCore] = useState(false);
  const [coreActive, setCoreActive] = useState(false);
  const [showMorph, setShowMorph] = useState(false);
  const [brand, setBrand] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setShowInputs(false);
      setShowCore(false);
      setCoreActive(false);
      setShowMorph(false);
      setPillCount(0);
      setBrand(0);
      setFadeOut(false);
      await sleep(400);

      // Pills stagger in
      setShowInputs(true);
      for (let i = 1; i <= 4; i++) {
        if (!loopRef.current) return;
        setPillCount(i);
        await sleep(250);
      }
      await sleep(600);

      // Fade inputs, show core
      setShowInputs(false);
      await sleep(500);

      setShowCore(true);
      await sleep(300);
      setCoreActive(true);
      await sleep(1500);

      // Fade core, show morph
      setShowCore(false);
      setCoreActive(false);
      await sleep(500);

      setShowMorph(true);
      setBrand(0);
      await sleep(2500);
      if (!loopRef.current) return;
      setBrand(1);
      await sleep(2500);
      if (!loopRef.current) return;
      setBrand(2);
      await sleep(2500);

      await sleep(3000);

      setFadeOut(true);
      await sleep(900);
      await sleep(500);
    }
  }, [sleep]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [runLoop]);

  return (
    <div className="custom-brand">
      <div className={`cb-stage${fadeOut ? ' cb-fade-out' : ''}`}>
        {/* Input pills */}
        <div className={`cb-section cb-inputs${showInputs ? ' cb-show' : ''}`}>
          <div className="cb-pill-grid">
            {INPUTS.map((inp, i) => (
              <div
                key={inp.label}
                className={`sc-pill cb-pill cb-pill-${i}${i < pillCount ? ' cb-pill-in' : ''}`}
              >
                <PillIcon type={inp.icon} />
                {inp.label}
              </div>
            ))}
          </div>
        </div>

        {/* AI core node */}
        <div className={`cb-section cb-core-wrap${showCore ? ' cb-show' : ''}`}>
          <div className={`cb-core-node${coreActive ? ' cb-core-pulse' : ''}`}>
            <div className="cb-core-glow" />
            <div className="cb-core-ring" />
            <div className="cb-core-dot" />
          </div>
        </div>

        {/* Brand morph */}
        <div className={`cb-section cb-morph${showMorph ? ' cb-show' : ''}`}>
          <div className="cb-morph-label sc-section-label">AI Brand Voice</div>
          <div className="cb-morph-stack">
            {BRANDS.map((b, i) => (
              <div
                key={b.name}
                className={`cb-brand${i === brand ? ' cb-brand-active' : ''}`}
                style={{ '--cb-accent': b.accent } as React.CSSProperties}
              >
                <div className="cb-brand-header">
                  <span className="cb-brand-dot" />
                  <span className="cb-brand-name">{b.name}</span>
                </div>
                <div
                  className="sc-bubble sc-bubble-assistant cb-brand-bubble"
                  style={{
                    fontFamily: b.font,
                    fontWeight: b.weight,
                    fontStyle: b.style,
                  }}
                >
                  {b.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
